<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed z-10 inset-0 overflow-y-auto"
            @close="open = false"
            :open="open"
        >
            <div
                class="
                    flex
                    items-end
                    justify-center
                    min-h-screen
                    pt-4
                    px-4
                    pb-20
                    text-center
                    sm:block sm:p-0
                "
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="
                            fixed
                            inset-0
                            bg-opacity-75
                            transition-all
                            duration-500
                            ease-in-out
                        "
                        :class="{
                            'bg-gray-500': !warning,
                            'bg-red-300': warning,
                        }"
                    />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="
                            inline-block
                            align-bottom
                            bg-white
                            rounded-lg
                            px-4
                            pt-5
                            pb-4
                            text-left
                            overflow-hidden
                            shadow-xl
                            transform
                            transition-all
                            sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6
                        "
                    >
                        <div
                            class="
                                mx-auto
                                flex
                                items-center
                                justify-center
                                h-10
                                w-10
                                rounded-full
                                bg-indigo-100
                                fixed
                                -mt-3
                                ml-auto
                                inset-x-0
                                top-0
                                hidden
                            "
                        >
                            <PencilAltIcon class="h-5 w-5 text-indigo-600" />
                        </div>
                        <div class="mt-2">
                            <div class="text-center sm:mt-0">
                                <DialogTitle
                                    as="h3"
                                    class="
                                        text-base
                                        leading-2
                                        font-medium
                                        text-gray-700
                                    "
                                >
                                    {{
                                        t("polls.results-header", "Stemmingen")
                                    }}
                                </DialogTitle>
                                <DialogTitle
                                    as="h3"
                                    class="
                                        text-lg
                                        leading-6
                                        font-medium
                                        text-gray-900
                                    "
                                >
                                    {{ meeting.title }}
                                </DialogTitle>

                                <DialogTitle
                                    as="h5"
                                    class="
                                        text-base
                                        leading-2
                                        pt-1
                                        font-medium
                                        text-gray-700
                                    "
                                >
                                    {{ meeting.datum }}
                                </DialogTitle>

                                <input
                                    type="text"
                                    tabIndex="{0}"
                                    :class="{ hidden: opened }"
                                />

                                <info-box
                                    v-if="polls && polls.length == 0"
                                    :message="message"
                                ></info-box>

                                <div class="mt-4 text-left">
                                    <div
                                        class="
                                            bg-gray-100
                                            shadow
                                            rounded-lg
                                            mb-6
                                            relative
                                        "
                                        v-for="(poll, index) in polls"
                                        v-bind:key="index"
                                    >
                                        <div class="p-2 px-4">
                                            {{ poll.question }}
                                        </div>
                                        <div class="bg-gray-50 p-2 px-4">
                                            <div
                                                class="answer"
                                                v-for="(
                                                    answer, subindex
                                                ) in poll.results"
                                                v-bind:key="subindex"
                                            >
                                                <span class="font-semibold">
                                                    <svg
                                                        v-if="
                                                            poll.has_voted ==
                                                            answer['answer']
                                                        "
                                                        class="
                                                            w-4
                                                            h-4
                                                            inline-block
                                                            font-light
                                                        "
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        ></path>
                                                    </svg>

                                                    {{ answer["answer"] }}
                                                    <span
                                                        class="
                                                            text-xs
                                                            text-gray-500
                                                        "
                                                        v-if="
                                                            answer['weight'] !=
                                                                'private' ||
                                                            can_initiate == true
                                                        "
                                                    >
                                                        &bull;
                                                        {{
                                                            answer[
                                                                "percentage"
                                                            ]
                                                        }}% &bull;
                                                        {{ answer["weight"] }}
                                                        {{
                                                            t(
                                                                "tts.polls.weight"
                                                            )
                                                        }}
                                                        &bull;
                                                        {{ answer["votes"] }}
                                                        {{
                                                            t("tts.polls.votes")
                                                        }}
                                                    </span>
                                                </span>
                                                <div
                                                    class="relative pt-1"
                                                    v-show="
                                                        poll.is_published ||
                                                        can_initiate == true
                                                    "
                                                >
                                                    <div
                                                        class="
                                                            overflow-hidden
                                                            h-2
                                                            mb-4
                                                            text-xs
                                                            flex
                                                            rounded
                                                            bg-blue-200
                                                            poll-empty
                                                        "
                                                    >
                                                        <div
                                                            :style="
                                                                'width:' +
                                                                answer[
                                                                    'percentage'
                                                                ] +
                                                                '%'
                                                            "
                                                            class="
                                                                shadow-none
                                                                flex flex-col
                                                                text-center
                                                                whitespace-nowrap
                                                                text-white
                                                                justify-center
                                                                bg-blue-600
                                                                poll-fill
                                                            "
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, PencilAltIcon } from "@heroicons/vue/outline";
import InfoBox from "../../../components/InfoBox.vue";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CheckIcon,
        InfoBox,
        PencilAltIcon,
    },
    mounted() {
        this.getPolls();
        Eventing.listen("closeModal", () => (this.open = false));
    },
    created() {
        this.message = this.t("polls.no-polls", "Geen stemmingen bekend");
    },
    data() {
        const open = ref(false);

        return {
            open,
            opened: false,
            meeting: null,
            warning: false,
            polls: null,
            message: "Geen stemmingen bekend",
        };
    },
    methods: {
        getPolls() {
            var self = this;
            axios
                .get(
                    "/api/rooms/" +
                        self.$route.params.id +
                        "?polls=true&results=true"
                )
                .then((response) => {
                    console.info(response.data.data);
                    this.polls = response.data.data;
                    this.meeting = response.data.meeting;
                    this.open = true;
                    setTimeout(() => {
                        self.opened = true;
                    }, 400);
                    Eventing.fire("formMeeting");
                })
                .catch((error) => {
                    // alert(error.response.status);
                });
        },
    },
};
</script>
